import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PING_INTERVAL_MS = 60_000; // 60s steady-state
const PING_TIMEOUT_MS = 6_000; // short, independent of the global axios timeout
const FAILURES_BEFORE_ALARM = 2; // require 2 consecutive failures so a single hiccup doesn't spook users

/**
 * Lightweight backend heartbeat. Pings /api/health on mount and every 60s.
 * Shows a sticky top banner after FAILURES_BEFORE_ALARM consecutive failures
 * so users know we're aware AND so they don't burn money on broken flows.
 *
 * Auto-clears the banner when the backend recovers. Manual dismiss persists
 * for the current tab session via sessionStorage.
 */
export default function HealthMonitor() {
  const [degraded, setDegraded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem("health_banner_dismissed") === "1";
  });

  useEffect(() => {
    if (!BACKEND_URL) return; // No backend wired — silent no-op
    let cancelled = false;
    let failures = 0;

    const tick = () => {
      // Independent client (NOT the shared `api` instance) so this never inherits
      // auth retries or interceptors that could mask a real outage.
      axios
        .get(`${BACKEND_URL}/api/health`, { timeout: PING_TIMEOUT_MS, withCredentials: false })
        .then(({ data }) => {
          if (cancelled) return;
          if (data && data.ok === true) {
            failures = 0;
            setDegraded((wasDegraded) => {
              // On recovery, also clear any stale "dismissed" flag so a future
              // outage is allowed to surface again.
              if (wasDegraded && typeof window !== "undefined") {
                window.sessionStorage.removeItem("health_banner_dismissed");
                setDismissed(false);
              }
              return false;
            });
          } else {
            failures += 1;
            if (failures >= FAILURES_BEFORE_ALARM) setDegraded(true);
          }
        })
        .catch(() => {
          if (cancelled) return;
          failures += 1;
          if (failures >= FAILURES_BEFORE_ALARM) setDegraded(true);
        });
    };

    tick(); // immediate check
    const id = setInterval(tick, PING_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!degraded || dismissed) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-[#FF3333] text-black border-b-2 border-black"
      data-testid="health-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 flex-wrap">
        <AlertTriangle size={16} className="shrink-0" />
        <div className="font-mono text-xs uppercase tracking-[0.15em] flex-1 min-w-0">
          <span className="font-bold">BACKEND DEGRADED</span> · Some flows (sessions, billing) may fail. We&apos;re on it — try again in a minute.
        </div>
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined") {
              window.sessionStorage.setItem("health_banner_dismissed", "1");
            }
            setDismissed(true);
          }}
          data-testid="health-banner-dismiss"
          className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold hover:underline"
          aria-label="Dismiss banner"
        >
          <X size={12} /> Dismiss
        </button>
      </div>
    </div>
  );
}
